import { Alert, Button, Form, FormFieldGroup, FormInput, FormLabel, FormSelect, Tooltip, RoleHelpPopover } from '$components'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { invitationIndexQueryKey, invitationStoreMutation } from '$api-client/@tanstack/react-query.gen'
import { Role } from '$api-client/types.gen'
import { ElementIntent } from '$components'
import { useModal } from '$components'
import { QueryErrorHelper } from '$utils/query'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { ToastHelper } from '$components'
import { ModelType } from '$components'

enum Input {
  FirstName = 'first_name',
  LastName = 'last_name',
  Email = 'email',
  Role = 'role',
}

export type InviteSchema = z.infer<typeof inviteSchema>

const inviteSchema = z.object({
  [Input.FirstName]: z.string().min(1, 'First name is required'),
  [Input.LastName]: z.string().min(1, 'Last name is required'),
  [Input.Email]: z.string().email('Please enter a valid email address'),
  [Input.Role]: z.object({
    id: z.string(),
    value: z.string(),
  }),
})

const roleOptions = Object.values(Role).map((role) => ({
  id: role,
  value: role,
})).filter((role) => role.id !== Role.SUPER_ADMIN)

export function InviteMemberForm() {
  const { close } = useModal()
  const queryClient = useQueryClient()
  const { hasSubscriptionExpired } = useTenant()

  const { mutateAsync: invite, isPending: isInviting, error } = useMutation({
    ...invitationStoreMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invitationIndexQueryKey() })
      ToastHelper.createSuccess(ModelType.TeamMemberInvitation)
      close()
    },
    onError: () => ToastHelper.createError(ModelType.TeamMemberInvitation),
  })

  const handleInviteUser = async (data: InviteSchema) => {
    await invite({
      body: {
        first_name: data[Input.FirstName],
        last_name: data[Input.LastName],
        email: data[Input.Email],
        role: data[Input.Role].id as Role,
      },
    })
  }

  const maxTeamMembersError = QueryErrorHelper.getQueryErrorByCode(error, 'max_team_members')

  return (
    <Form<InviteSchema, typeof inviteSchema>
      schema={inviteSchema}
      onSubmit={handleInviteUser}
      errors={QueryErrorHelper.getQueryValidationErrors(error)}
      defaultValues={{
        [Input.Role]: {
          id: Role.GROOMER,
          value: 'Groomer',
        },
      }}
    >
      {() => {
        return (
          <div className="flex flex-col gap-stack-section h-full">
            <FormFieldGroup>
              <FormLabel htmlFor={Input.FirstName}>
                First Name
              </FormLabel>

              <FormInput
                name={Input.FirstName}
                type="text"
                placeholder="Enter first name"
              />
            </FormFieldGroup>

            <FormFieldGroup>
              <FormLabel htmlFor={Input.LastName}>
                Last Name
              </FormLabel>

              <FormInput
                name={Input.LastName}
                type="text"
                placeholder="Enter last name"
              />
            </FormFieldGroup>

            <FormFieldGroup>
              <FormLabel htmlFor={Input.Email}>
                Email
              </FormLabel>

              <FormInput
                name={Input.Email}
                type="email"
                placeholder="Enter email address"
              />
            </FormFieldGroup>

            <FormFieldGroup>
              <div className="flex items-center gap-stack-tight">
                <FormLabel htmlFor={Input.Role}>
                  Role
                </FormLabel>
                <RoleHelpPopover />
              </div>

              <FormSelect
                name={Input.Role}
                placeholder="Select a role"
                options={roleOptions}
              />
            </FormFieldGroup>

            { maxTeamMembersError && (
              <Alert intent={ElementIntent.Danger}>
                <Alert.Heading>
                  Failed to invite team member
                </Alert.Heading>

                <Alert.Body>
                  { maxTeamMembersError.message }
                </Alert.Body>
              </Alert>
            )}

            <div className="flex gap-stack-roomy mt-auto ml-auto">
              <Button
                type="button"
                variant="ghost"
                size="lg"
                onClick={close}
              >
                Cancel
              </Button>

              <Tooltip
                content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
                disabled={!hasSubscriptionExpired}
              >
                <Button
                  type="submit"
                  size="lg"
                  isLoading={isInviting}
                  disabled={hasSubscriptionExpired || isInviting}
                >
                  Send Invitation
                </Button>
              </Tooltip>
            </div>
          </div>
        )
      }}
    </Form>
  )
}
